import Router, { useRouter } from "next/router";
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { CareerLevel } from '../../../../shared/model/CareerLevel';
import { Categories } from "../../../../shared/model/Categories";
import { EmploymentType } from "../../../../shared/model/EmploymentType";
import { RemoteType } from "../../../../shared/model/RemoteType";
import { device } from "../../../../shared/styles/device";
import { FilterCheckbox } from "../../../../shared/styles/input";
import { enumKeys } from '../../../../shared/utils/enumkeys';
import FilterSVG from '/public/svg/jobs/filter.svg';

interface JobsFilterWrapperProps {
    open: boolean;
}

const JobsFilterWrapper = styled.div<JobsFilterWrapperProps>`

    max-height: ${props => props.open ? "3000px" : "0px"};
    height: auto;
    list-style-type: none;
    padding: 0;
    margin: 0;
    overflow: hidden;
    display: block;
    -webkit-transition: max-height 0.25s cubic-bezier(0.865,0.14,0.095,0.87);
    transition: max-height 0.25s cubic-bezier(0.865,0.14,0.095,0.87);


    @media ${device.tablet} {
        max-height: unset;
    }

`;
const JobsFilterMobileDropdown = styled.div`
    display: flex;
    align-items: center;
    font-weight: 700;
    font-size: 16px;
    line-height: 20px;
    color: ${props => props.theme.colors.buttonPrimary};
    margin: auto;

    svg {
        height: 20px;
        width: 20px;

        &>* {
            fill: ${props => props.theme.colors.buttonPrimary};
        }
    
        margin-right: 8px;
    }

    @media ${device.tablet} {
        display: none;
        height: 0;
    }
`;
const JobsFilterContainer = styled.div`
    display: flex;
    flex-direction: column;
    row-gap: 12px;
`;

const JobsFilterCategory = styled.div`
    display: flex;
    flex-direction: column;

    p {
        margin-top: 0;
        margin-bottom: 10px;
    }
    
    strong {
        font-weight: 700;
        font-size: 16px;
        line-height: 20px;
    }
`;

const categories = enumKeys(Categories).map((categoryKey) => {
    return {
        key: categoryKey,
        name: Categories[categoryKey],
        isChecked: false
    }
});

const careerLevel = enumKeys(CareerLevel).map((carrerKey) => {
    return {
        key: carrerKey,
        name: CareerLevel[carrerKey],
        isChecked: false
    }
});

const remoteType = enumKeys(RemoteType).map((remoteKey) => {
    return {
        key: remoteKey,
        name: RemoteType[remoteKey],
        isChecked: false
    }
});

const employmentType = enumKeys(EmploymentType).map((employmentKey) => {
    return {
        key: employmentKey,
        name: EmploymentType[employmentKey],
        isChecked: false
    }
});

export interface JobsFilterProps {
    searchAggregates: any[] | null | undefined;
}

export default function JobsFilter({ searchAggregates }: JobsFilterProps) {

    const initialFilters = {
        categories,
        careerLevel,
        remoteType,
        employmentType
    }

    const router = useRouter()
    const [filters, setFilters] = useState(initialFilters);
    const [filtersDropdownOpen, setFiltersDropdownOpen] = useState(false);

    useEffect(() => {
        let searchParams = new URLSearchParams(window.location.search);

        const filtersUpdate = filters;
        setFilterCheckedBySearchParams(filtersUpdate.categories, searchParams, 'categories');
        setFilterCheckedBySearchParams(filtersUpdate.careerLevel, searchParams, 'careerLevel');
        setFilterCheckedBySearchParams(filtersUpdate.employmentType, searchParams, 'employmentType');
        setFilterCheckedBySearchParams(filtersUpdate.remoteType, searchParams, 'remoteType');
        setFilters(filtersUpdate);

        console.log("FILTER useEffect", filtersUpdate);
    }, [router])

    const handleChange = (key, type) => {
        const filtersUpdate = filters;
        const category = filtersUpdate[type].find((obj) => obj.key === key);
        if (category) {
            category.isChecked = !category.isChecked
        }
        setFilters(filtersUpdate)
        updateRoute();
    };

    const updateRoute = () => {
        let urlSearchParams = new URLSearchParams(window.location.search);

        addParamToURLSearchParams(urlSearchParams, buildParamsFromCheckboxes(filters.categories), 'categories');
        addParamToURLSearchParams(urlSearchParams, buildParamsFromCheckboxes(filters.careerLevel), 'careerLevel');
        addParamToURLSearchParams(urlSearchParams, buildParamsFromCheckboxes(filters.remoteType), 'remoteType');
        addParamToURLSearchParams(urlSearchParams, buildParamsFromCheckboxes(filters.employmentType), 'employmentType');

        Router.push('/jobs/search?' + urlSearchParams.toString());
    }

    const setFilterCheckedBySearchParams = (filterField, searchParams, key) => {
        const searchParamsCategories: any = searchParams?.get(key);
        filterField?.forEach((category) => {
            if (searchParamsCategories?.includes(category.key)) category.isChecked = true;
        })
    }

    const buildParamsFromCheckboxes = (checkboxes) => {
        return checkboxes.filter((cat) => cat.isChecked).map((cat) => cat.key).join(',');
    }

    const addParamToURLSearchParams = (urlSearchParams, param, key) => {
        if (param != null && param?.length > 0) urlSearchParams.set(key, param); else urlSearchParams.delete(key);
    }

    const findKeyInSearchAggregates = (filterName, key): number | undefined => {
        return searchAggregates?.find((aggregate) => aggregate.name === filterName)?.result?.buckets?.find((bucket) => bucket?.key === key)?.doc_count;
    }

    return (
        <JobsFilterContainer>
            <JobsFilterMobileDropdown onClick={() => setFiltersDropdownOpen(!filtersDropdownOpen)}>
                <FilterSVG /> Ergebnisse filtern
            </JobsFilterMobileDropdown>
            <JobsFilterWrapper open={filtersDropdownOpen}>
                <JobsFilterCategory>
                    <p><strong>Kategorien</strong></p>
                    {filters.categories.map((category, index) => {
                        return (
                            <Checkbox
                                key={category.key + index}
                                label={category.name}
                                value={category.isChecked}
                                aggregate={findKeyInSearchAggregates("category", category.name)}
                                onChange={() => handleChange(category.key, "categories")}
                            />
                        )

                    })}
                </JobsFilterCategory>
                <JobsFilterCategory>
                    <p><strong>Karrierestufen</strong></p>
                    {filters.careerLevel.map((careerLevel, index) => {
                        return (
                            <Checkbox
                                key={careerLevel.key + index}
                                label={careerLevel.name}
                                value={careerLevel.isChecked}
                                aggregate={findKeyInSearchAggregates("careerLevel", careerLevel.name)}
                                onChange={() => handleChange(careerLevel.key, "careerLevel")}
                            />
                        )

                    })}
                </JobsFilterCategory>
                <JobsFilterCategory>
                    <p><strong>Beschäftigungsart</strong></p>
                    {filters.employmentType.map((employmentType, index) => {
                        return (
                            <Checkbox
                                key={employmentType.key + index}
                                label={employmentType.name}
                                value={employmentType.isChecked}
                                aggregate={findKeyInSearchAggregates("employmentType", employmentType.name)}
                                onChange={() => handleChange(employmentType.key, "employmentType")}
                            />
                        )

                    })}
                </JobsFilterCategory>
                <JobsFilterCategory>
                    <p><strong>Homeoffice</strong></p>
                    {filters.remoteType.map((remoteType, index) => {
                        return (
                            <Checkbox
                                key={remoteType.key + index}
                                label={remoteType.name}
                                value={remoteType.isChecked}
                                aggregate={findKeyInSearchAggregates("remoteType", remoteType.name)}
                                onChange={() => handleChange(remoteType.key, "remoteType")}
                            />
                        )

                    })}
                </JobsFilterCategory>
            </JobsFilterWrapper>
        </JobsFilterContainer>
    )
}

interface CheckboxProps {
    label: string;
    value: any;
    onChange: any;
    aggregate?: number | null | undefined;
}

const Checkbox = ({ label, value, onChange, aggregate }: CheckboxProps) => {
    return (
        <FilterCheckbox>
            <label>
                <input type="checkbox" value={value} checked={value} onChange={onChange} />
                {label} {aggregate != null ? '(' + aggregate + ')' : ''}
            </label>
        </FilterCheckbox>
    );
};
